import { Avatar, Box, IconButton, Tooltip } from "@mui/material";
import { TypeText, VersionDetailsBox } from "./styles";
import { NameText, TimeBox, styles } from "./styles";
import { DateText, InlineSize, LabelDateBox, LabelText } from "./styles";
import DeleteIcon from "@mui/icons-material/Delete";
import { TrackFilesProps, month, FileTypeMap } from "./types";
import { useContext, useEffect, useState } from "react";
import { useGetUser } from "../../hooks/useGetUser";
import { User } from "../../proto/api_pb";
import { NavLink, useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadSong } from "../TrackComponents/TrackHandler/trackHandlers";
import { handleCreateGroupChat } from "../TrackComponents/TrackHandler/trackHandlers";
import { AuthContext, TalkJsContext } from "../../contexts";
import { TalkJsContextInterface } from "../../contexts";
import { LightTooltip } from "../LightToolTip/LightToolTip";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { ENTERPRISE_ARTWORK_TEMPLATE, isEnterprise } from "../../theme";
import MessageIcon from "@mui/icons-material/Message";
import LinkIcon from "@mui/icons-material/Link";
import ShareIcon from "@mui/icons-material/Share";

export const TrackFiles: React.FC<TrackFilesProps> = ({
  url,
  label,
  timeStamp,
  ownerId,
  fileName,
  fileType,
  fileSize,
  fileId,
  handleOpenDeleteModal,
  handleOpenCollabsModal,
  collaborators,
  file,
  allowDownload,
}) => {
  const [hover, setHover] = useState<boolean>(false);
  const { getUserResponse } = useGetUser(ownerId);
  const [owner, setOwner] = useState<User>();
  const [createGroupChatClicked, setCreateGroupChatClicked] = useState(false);
  const talkJsContext = useContext<TalkJsContextInterface>(TalkJsContext);
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();
  const date = new Date(timeStamp * 1000);
  const year = date.getFullYear();
  const mm = month[date.getMonth()];
  const dd = date.getDate();
  const isSharedContentPage = window.location.pathname.includes("/share/");

  const createGroupChat = (
    id: string | undefined,
    title: string | undefined
  ) => {
    handleCreateGroupChat(
      talkJsContext,
      collaborators,
      navigate,
      setCreateGroupChatClicked,
      id,
      title
    );
  };

  useEffect(() => {
    setOwner(getUserResponse);
  }, [getUserResponse]);

  useEffect(() => {
    if (createGroupChatClicked && talkJsContext) {
      talkJsContext.createConversation(fileId, fileName);
    }
  }, [createGroupChatClicked, fileId, fileName, talkJsContext]);

  return (
    <>
      <VersionDetailsBox
        style={{
          paddingTop: 15,
          boxShadow: hover
            ? "0px 10px 20px 2px rgba(0,0,0,0.75)"
            : "0px 5px 10px 0px rgba(0,0,0,0.7)",
          transform: hover ? `translate(0,-5px)` : `translate(0,0)`,
        }}
        onClick={() => {
          navigate(`/file/${fileId}`);
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {owner && (
          <NavLink
            onClick={(e) => e.stopPropagation()}
            key={owner.getUserId()}
            to={`/user-profile/${owner.getUsername()}`}
            style={styles.contributorNavLink}
          >
            <Tooltip
              title={`Uploaded by ${owner.getUsername()}`}
              placement="top"
            >
              <Avatar
                style={styles.contributorsPhotos}
                src={
                  owner.getProfilePicture()
                    ? owner.getProfilePicture()
                    : isEnterprise
                    ? ENTERPRISE_ARTWORK_TEMPLATE
                    : "/assets/images/logo512.png"
                }
                alt={owner.getUsername()}
              />
            </Tooltip>
          </NavLink>
        )}
        <LabelDateBox>
          <Tooltip title={fileName} placement="top">
            <NameText noWrap>{fileName}</NameText>
          </Tooltip>
          <LabelText>{label ? label : "----"}</LabelText>
          <TypeText>{FileTypeMap[fileType]}</TypeText>
          <Box sx={{ display: "flex" }}>
            <DateText noWrap>{`${mm} ${dd} ${year}`}</DateText>
            <InlineSize>&nbsp;/&nbsp;{fileSize}</InlineSize>
          </Box>
        </LabelDateBox>
        <TimeBox>{fileSize}</TimeBox>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={styles.iconButtonsContainer}
        >
          {(!isSharedContentPage || (isSharedContentPage && allowDownload)) && (
            <IconButton
              sx={styles.downloadIconButton}
              onClick={() => downloadSong(url, label, "", "", "")}
            >
              <DownloadIcon />
            </IconButton>
          )}
          {handleOpenCollabsModal && !isSharedContentPage && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (handleOpenCollabsModal) {
                  handleOpenCollabsModal(fileId!);
                }
              }}
              sx={styles.iconColor}
            >
              <ManageAccountsIcon />
            </IconButton>
          )}
          {!isSharedContentPage && (
            <Tooltip title="Message" placement="bottom">
              <IconButton
                sx={styles.messageIconButton}
                onClick={(e) => {
                  e.stopPropagation();
                  createGroupChat(fileId, label);
                }}
              >
                <MessageIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isSharedContentPage && (
            <Tooltip title="Link File With Tracks" placement="bottom">
              <IconButton sx={styles.iconColor} onClick={() => {}}>
                <LinkIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isSharedContentPage && (
            <Tooltip title="Share" placement="bottom">
              <IconButton sx={styles.iconColor} onClick={() => {}}>
                <ShareIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isSharedContentPage && (
            <LightTooltip
              enterTouchDelay={0}
              enterDelay={300}
              title={userId !== ownerId ? "Only owner can delete the file" : ""}
              placement="top"
            >
              {/* using span so that tooltip shows on disabled button */}
              <span>
                <IconButton
                  sx={styles.deleteIconButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (handleOpenDeleteModal && fileId)
                      handleOpenDeleteModal(fileId);
                  }}
                  disabled={ownerId !== userId}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </LightTooltip>
          )}
        </Box>
      </VersionDetailsBox>
    </>
  );
};
